import React from 'react';

/* ─── STATIONS ────────────────────────────────────────────────────────────
   One installation per service, drawn.

   ── what makes drawn blocks look cheap, and what fixes it ──

   The first version of this was flat fills: a rectangle of accent at 30%,
   another at 42%, little white squares for windows. That reads as a wireframe
   whatever you build out of it, and no amount of extra shapes rescues it. The
   reference's own lesson applies here as much as it did to its hills —

     1. NOTHING IS FLAT. Every surface in that scene is a gradient. A block
        with a light top and a deeper base has a form; a block filled with one
        colour is a swatch.
     2. VALUE CARRIES DEPTH. Far blocks sit light and low-contrast, near ones
        deepen. That reads before any shape is identified.
     3. SILHOUETTE IS THE WHOLE READ at this size. Setbacks, a mast, a roof
        unit, a canopy — a profile with three changes of direction looks
        designed; a plain rectangle looks unfinished.

   So everything here is built from one primitive, `Blk`, which is a rounded
   block filled with a vertical gradient and given a light-catch down its lit
   edge. The kits below compose those into recognisable installations, and the
   detail is kept to what survives at this scale: window rows, roof plant, a
   mast, a canopy.

   ── how they are drawn ──

   Local coordinates, origin at the centre of the base ON THE GROUND LINE.
   Structures build upward, so y is negative going up. That lets the stage drop
   a station anywhere along the row without knowing what is in it.

   `data-lit` on the station group is the live state and CSS owns what it
   MEANS — windows warm to the accent, glows come up. One attribute per station
   rather than a tween per window keeps a six-service sector at six state
   changes instead of forty. */

/* ── the primitive ────────────────────────────────────────────────────────
   `g` is the gradient id for this chapter; `tone` picks how solid the block
   sits, which is how a single kit reads as having depth within itself. */
export const Blk = ({ x, y, w, h, g, tone = 'mid', r = 3 }) => (
  <>
    <rect x={x} y={y} width={w} height={h} rx={r}
      fill={`url(#${g})`} className={`zx-blk zx-blk--${tone}`} />
    {/* light catch down the lit edge — one hairline, and it is the difference
        between a panel and a rectangle */}
    <rect x={x} y={y + r} width="1.5" height={Math.max(0, h - r * 2)}
      className="zx-blk__edge" />
  </>
);

/* A row of windows. `lit` marks the ones that warm up when this is the
   installation being shown. */
export function Row({ x, y, n, gap = 17, w = 8, h = 11, lit = false }) {
  return <>{Array.from({ length: n }, (_, i) => (
    <rect key={i} x={x + i * gap} y={y} width={w} height={h} rx="1"
      className={lit ? 'zx-win zx-win--lit' : 'zx-win'} />
  ))}</>;
}

/* Roof plant — the small box-and-mast clutter that stops a flat roof looking
   like a cut-off. */
const Roof = ({ x, y, g, mast = 0 }) => (
  <>
    <Blk x={x} y={y - 9} w={26} h={9} g={g} tone="near" r={1.5} />
    <Blk x={x + 32} y={y - 6} w={14} h={6} g={g} tone="near" r={1.5} />
    {mast > 0 && (
      <>
        <rect x={x + 12} y={y - 9 - mast} width="2.5" height={mast} className="zx-blk__mast" />
        <circle cx={x + 13} cy={y - 11 - mast} r="3" className="zx-win zx-win--lit" />
      </>
    )}
  </>
);

/* A soft pad under the installation, so it sits on the ground rather than
   floating above it. */
const Pad = ({ half = 130 }) => (
  <ellipse className="zx-st__pad" cx="0" cy="2" rx={half} ry={half * 0.11} />
);

/* ════════════════════════════════════════════════════════════════════════
   01 · HEALTHCARE
   ════════════════════════════════════════════════════════════════════════ */
const HEALTHCARE = g => [
  // Remote patient monitoring — ward block, live trace on the roof screen
  () => (
    <>
      <Pad half={132} />
      <Blk x={-96} y={-186} w={118} h={186} g={g} tone="mid" />
      <Blk x={26} y={-138} w={70} h={138} g={g} tone="far" />
      <Blk x={-62} y={-224} w={52} h={38} g={g} tone="near" />
      <path className="zx-st__trace"
        d="M -52 -205 L -30 -205 L -22 -216 L -12 -193 L -2 -209 L 8 -205 L 30 -205" />
      <Roof x={30} y={-138} g={g} mast={26} />
      <Row x={-84} y={-166} n={5} lit />
      <Row x={-84} y={-138} n={5} />
      <Row x={-84} y={-110} n={5} />
      <Row x={-84} y={-82} n={5} />
      <Blk x={-30} y={-42} w={44} h={42} g={g} tone="near" r={2} />
    </>
  ),
  // Phototherapy — canopy over an incubator bay
  () => (
    <>
      <Pad half={120} />
      <Blk x={-84} y={-124} w={168} h={124} g={g} tone="mid" />
      <rect x={-58} y={-196} width="7" height="72" className="zx-blk__mast" />
      <rect x={51} y={-196} width="7" height="72" className="zx-blk__mast" />
      <Blk x={-70} y={-210} w={140} h={16} g={g} tone="near" r={5} />
      <rect className="zx-st__lamp" x={-58} y={-192} width="116" height="8" rx="4" />
      <path className="zx-st__glow" d="M -58 -184 L 58 -184 L 82 -124 L -82 -124 Z" />
      <Blk x={-46} y={-160} w={92} h={34} g={g} tone="far" r={6} />
      <Row x={-64} y={-98} n={5} gap={27} lit />
      <Row x={-64} y={-66} n={5} gap={27} />
    </>
  ),
  // Neonatal — low ward under a pitched roof, monitor mast
  () => (
    <>
      <Pad half={140} />
      <Blk x={-122} y={-132} w={244} h={132} g={g} tone="mid" />
      <path d="M -134 -132 L 0 -178 L 134 -132 Z" fill={`url(#${g})`} className="zx-blk zx-blk--near" />
      <rect x={-3} y={-222} width="5" height="46" className="zx-blk__mast" />
      <circle className="zx-win zx-win--lit" cx="0" cy="-226" r="6" />
      <Row x={-104} y={-112} n={4} gap={24} lit />
      <Row x={8} y={-112} n={4} gap={24} />
      <Blk x={-64} y={-62} w={52} h={34} g={g} tone="far" r={4} />
      <Blk x={12} y={-62} w={52} h={34} g={g} tone="far" r={4} />
    </>
  ),
  // Nutrition & digital health — clinic with a data wall
  () => (
    <>
      <Pad half={126} />
      <Blk x={-108} y={-152} w={146} h={152} g={g} tone="mid" />
      <Blk x={42} y={-104} w={66} h={104} g={g} tone="far" />
      <Blk x={-94} y={-138} w={118} h={68} g={g} tone="near" r={3} />
      {/* the chart on the wall */}
      <rect className="zx-st__lamp" x={-84} y={-96} width="15" height="18" rx="1.5" />
      <rect className="zx-st__lamp" x={-63} y={-110} width="15" height="32" rx="1.5" />
      <rect className="zx-st__lamp" x={-42} y={-124} width="15" height="46" rx="1.5" />
      <rect className="zx-win" x={-21} y={-104} width="15" height="26" rx="1.5" />
      <Roof x={-40} y={-152} g={g} />
      <Row x={54} y={-84} n={2} gap={24} />
      <Row x={-94} y={-52} n={5} gap={24} />
    </>
  ),
  // Connected medical devices — equipment tower, devices linked up it
  () => (
    <>
      <Pad half={110} />
      <Blk x={-62} y={-214} w={124} h={214} g={g} tone="mid" r={4} />
      {[0, 1, 2, 3].map(i => (
        <rect key={i} className={i < 3 ? 'zx-st__lamp' : 'zx-win'}
          x={-44} y={-198 + i * 48} width="88" height="30" rx="2.5" />
      ))}
      <path className="zx-st__link" d="M -62 -178 L -94 -178 L -94 -62 L -62 -62" />
      <path className="zx-st__link" d="M 62 -130 L 94 -130 L 94 -34 L 62 -34" />
      <circle className="zx-win zx-win--lit" cx="-94" cy="-178" r="5" />
      <circle className="zx-win zx-win--lit" cx="94" cy="-130" r="5" />
      <Roof x={-24} y={-214} g={g} mast={20} />
    </>
  ),
  // spare — pharmacy / lab
  () => (
    <>
      <Pad half={120} />
      <Blk x={-100} y={-164} w={200} h={164} g={g} tone="mid" />
      <Blk x={-34} y={-198} w={68} h={34} g={g} tone="near" r={2} />
      <Row x={-84} y={-144} n={5} gap={22} lit />
      <Row x={-84} y={-112} n={5} gap={22} />
      <Row x={-84} y={-80} n={5} gap={22} />
    </>
  ),
];

/* ════════════════════════════════════════════════════════════════════════
   02 · UTILITIES & ENERGY
   ════════════════════════════════════════════════════════════════════════ */
const ENERGY = g => [
  // Energy monitoring — substation: transformer, bushings, control room
  () => (
    <>
      <Pad half={144} />
      <Blk x={-92} y={-124} w={176} h={124} g={g} tone="mid" r={4} />
      {[-62, -8, 46].map(x => (
        <React.Fragment key={x}>
          <rect x={x} y={-158} width="14" height="34" className="zx-blk__mast" />
          <circle className="zx-st__lamp" cx={x + 7} cy="-164" r="6.5" />
        </React.Fragment>
      ))}
      <Blk x={92} y={-98} w={54} h={98} g={g} tone="far" />
      <Row x={102} y={-82} n={2} gap={20} lit />
      <Row x={-76} y={-96} n={4} gap={30} />
      {[-142, -118, 118, 142].map(x => (
        <rect key={x} x={x} y="-48" width="3.5" height="48" className="zx-blk__mast" />
      ))}
    </>
  ),
  // Battery & storage — container bank
  () => (
    <>
      <Pad half={150} />
      {[-1, 0, 1].map(k => (
        <g key={k} transform={`translate(${k * 98} 0)`}>
          <Blk x={-46} y={-96} w={92} h={96} g={g} tone={k === 0 ? 'mid' : 'far'} r={3} />
          <rect className={k < 1 ? 'zx-st__lamp' : 'zx-win'} x={-32} y={-80} width="64" height="9" rx="2" />
          <rect className={k < 1 ? 'zx-st__lamp' : 'zx-win'} x={-32} y={-62} width="64" height="9" rx="2" />
          <rect className="zx-win" x={-32} y={-44} width="64" height="9" rx="2" />
        </g>
      ))}
      <Blk x={-12} y={-138} w={24} h={42} g={g} tone="near" r={2} />
      <circle className="zx-win zx-win--lit" cx="0" cy="-144" r="5.5" />
    </>
  ),
  // Renewable / solar — tilted array and an inverter hut
  () => (
    <>
      <Pad half={150} />
      {[0, 1, 2, 3].map(i => (
        <path key={i}
          className={i < 3 ? 'zx-st__panel zx-st__panel--lit' : 'zx-st__panel'}
          d={`M ${-148 + i * 76} 0 L ${-126 + i * 76} -78 L ${-58 + i * 76} -78
              L ${-80 + i * 76} 0 Z`} />
      ))}
      {[0, 1, 2, 3].map(i => (
        <rect key={i} x={-118 + i * 76} y="-42" width="4.5" height="42" className="zx-blk__mast" />
      ))}
      <Blk x={98} y={-90} w={56} h={90} g={g} tone="mid" r={3} />
      <Row x={110} y={-74} n={2} gap={20} lit />
      <Roof x={104} y={-90} g={g} />
    </>
  ),
  // Wind — turbine and a control kiosk
  () => (
    <>
      <Pad half={132} />
      <path d="M -52 0 L -45 -204 L -35 -204 L -28 0 Z" className="zx-blk__mast" />
      <circle cx="-40" cy="-206" r="7" className="zx-blk__mast" />
      {[0, 120, 240].map(a => (
        <rect key={a} className="zx-st__blade" x="-43" y="-296" width="6" height="92" rx="3"
          transform={`rotate(${a} -40 -206)`} />
      ))}
      <Blk x={54} y={-102} w={82} h={102} g={g} tone="mid" r={3} />
      <rect className="zx-st__lamp" x={66} y={-86} width="58" height="36" rx="3" />
      <Row x={68} y={-40} n={2} gap={26} />
    </>
  ),
  // DG automation — generator set, exhaust, transfer panel
  () => (
    <>
      <Pad half={140} />
      <Blk x={-114} y={-110} w={190} h={110} g={g} tone="mid" r={5} />
      <rect x={-76} y={-176} width="17" height="66" className="zx-blk__mast" />
      <Blk x={-82} y={-188} w={29} h={12} g={g} tone="near" r={3} />
      <path className="zx-st__glow" d="M -78 -190 q 15 -32 2 -56 q 24 20 13 56 Z" />
      <rect className="zx-st__lamp" x={-92} y={-92} width="146" height="13" rx="3" />
      <Row x={-88} y={-62} n={5} gap={28} />
      <Blk x={88} y={-130} w={56} h={130} g={g} tone="far" r={3} />
      <rect className="zx-st__lamp" x={98} y={-114} width="36" height="20" rx="2" />
      <rect className="zx-win" x={98} y={-86} width="36" height="20" rx="2" />
    </>
  ),
  // Predictive maintenance — pylon with a sensor cabinet
  () => (
    <>
      <Pad half={140} />
      <path d="M -70 0 L -24 -198 L -15 -198 L -47 0 Z" className="zx-blk__mast" />
      <path d="M 70 0 L 24 -198 L 15 -198 L 47 0 Z" className="zx-blk__mast" />
      <rect x={-88} y={-184} width="176" height="5" className="zx-blk__mast" />
      <rect x={-64} y={-214} width="128" height="5" className="zx-blk__mast" />
      <rect x={-40} y={-140} width="80" height="4" className="zx-blk__mast" opacity="0.7" />
      <circle className="zx-st__lamp" cx="-74" cy="-194" r="5.5" />
      <circle className="zx-st__lamp" cx="74" cy="-194" r="5.5" />
      <circle className="zx-st__lamp" cx="0" cy="-222" r="5.5" />
      <Blk x={92} y={-70} w={48} h={70} g={g} tone="mid" r={3} />
      <rect className="zx-st__lamp" x={100} y={-58} width="32" height="22" rx="2" />
    </>
  ),
];

/* ════════════════════════════════════════════════════════════════════════
   03 · INDUSTRIAL IoT & AUTOMATION
   ════════════════════════════════════════════════════════════════════════ */
const INDUSTRIAL = g => [
  // Smart factory — sawtooth hall
  () => (
    <>
      <Pad half={148} />
      <Blk x={-140} y={-130} w={280} h={130} g={g} tone="mid" r={2} />
      {[0, 1, 2, 3].map(i => (
        <path key={i} d={`M ${-140 + i * 70} -130 L ${-140 + i * 70} -172 L ${-70 + i * 70} -130 Z`}
          fill={`url(#${g})`} className="zx-blk zx-blk--near" />
      ))}
      <Row x={-120} y={-104} n={7} gap={35} lit />
      <Row x={-120} y={-72} n={7} gap={35} />
      <rect x={106} y={-216} width="19" height="86" className="zx-blk__mast" />
      <path className="zx-st__glow" d="M 110 -228 q 15 -34 2 -58 q 25 22 13 58 Z" />
    </>
  ),
  // Predictive maintenance — fenced machine with a sensor mast
  () => (
    <>
      <Pad half={136} />
      <Blk x={-96} y={-122} w={156} h={122} g={g} tone="mid" r={4} />
      <Blk x={-68} y={-158} w={64} h={36} g={g} tone="near" r={3} />
      <circle className="zx-st__lamp" cx="-36" cy="-174" r="7.5" />
      <rect x={78} y={-192} width="7" height="192" className="zx-blk__mast" />
      <rect className="zx-st__lamp" x={66} y={-204} width="31" height="13" rx="4" />
      <path className="zx-st__link" d="M 78 -156 L 22 -156" />
      <Row x={-80} y={-98} n={4} gap={31} lit />
      {[-132, -110, 110, 132].map(x => (
        <rect key={x} x={x} y="-54" width="3.5" height="54" className="zx-blk__mast" />
      ))}
    </>
  ),
  // Data acquisition — control room, wall of screens
  () => (
    <>
      <Pad half={142} />
      <Blk x={-122} y={-158} w={244} h={158} g={g} tone="mid" r={4} />
      <Blk x={-104} y={-142} w={208} h={80} g={g} tone="near" r={3} />
      {[0, 1, 2, 3].map(i => (
        <rect key={i} className="zx-st__lamp" x={-95 + i * 50} y="-134" width="40" height="28" rx="2" />
      ))}
      {[0, 1, 2, 3].map(i => (
        <rect key={i} className={i < 2 ? 'zx-st__lamp' : 'zx-win'}
          x={-95 + i * 50} y="-98" width="40" height="13" rx="2" />
      ))}
      <Row x={-100} y={-46} n={6} gap={35} />
      <Roof x={-30} y={-158} g={g} mast={22} />
    </>
  ),
  // Automation & control — robot cell behind a fence
  () => (
    <>
      <Pad half={142} />
      <Blk x={-122} y={-18} w={244} h={18} g={g} tone="mid" r={3} />
      <Blk x={-26} y={-50} w={52} h={34} g={g} tone="near" r={4} />
      <rect className="zx-st__arm" x={-15} y={-124} width="30" height="80" rx="9"
        transform="rotate(-18 0 -50)" />
      <rect className="zx-st__arm" x={6} y={-182} width="26" height="66" rx="9"
        transform="rotate(24 19 -120)" />
      <Blk x={44} y={-192} w={32} h={15} g={g} tone="near" r={4} />
      <Blk x={-128} y={-40} w={88} h={11} g={g} tone="far" r={3} />
      <rect className="zx-win zx-win--lit" x={-116} y={-55} width="19" height="15" rx="1.5" />
      <rect className="zx-win" x={-82} y={-55} width="19" height="15" rx="1.5" />
      {[-138, -112, 112, 138].map(x => (
        <rect key={x} x={x} y="-100" width="3.5" height="100" className="zx-blk__mast" />
      ))}
    </>
  ),
  // Electrical asset monitoring — switchgear line-up
  () => (
    <>
      <Pad half={146} />
      {[0, 1, 2, 3].map(k => (
        <g key={k} transform={`translate(${-110 + k * 74} 0)`}>
          <Blk x={-34} y={-142} w={68} h={142} g={g} tone={k % 2 ? 'far' : 'mid'} r={3} />
          <rect className={k < 2 ? 'zx-st__lamp' : 'zx-win'} x={-22} y={-128} width="44" height="17" rx="2" />
          <rect className="zx-win" x={-22} y={-100} width="44" height="11" rx="2" />
        </g>
      ))}
      <Blk x={-120} y={-158} w={240} h={12} g={g} tone="near" r={3} />
    </>
  ),
  // IoT platform — tanks and a comms mast
  () => (
    <>
      <Pad half={144} />
      <Blk x={-128} y={-130} w={78} h={130} g={g} tone="far" r={11} />
      <Blk x={-36} y={-158} w={78} h={158} g={g} tone="mid" r={11} />
      <Row x={-116} y={-110} n={2} gap={28} />
      <Row x={-24} y={-138} n={2} gap={28} lit />
      <rect x={98} y={-208} width="7" height="208" className="zx-blk__mast" />
      <Blk x={84} y={-216} w={36} h={9} g={g} tone="near" r={3} />
      <circle className="zx-st__lamp" cx="101" cy="-224" r="7" />
      <path className="zx-st__link" d="M 98 -176 L 42 -176" />
    </>
  ),
];

/* ════════════════════════════════════════════════════════════════════════
   04 · EDUCATION
   ════════════════════════════════════════════════════════════════════════ */
const EDUCATION = g => [
  // Smart classrooms — main hall with a flag
  () => (
    <>
      <Pad half={146} />
      <Blk x={-126} y={-150} w={252} h={150} g={g} tone="mid" />
      <path d="M -140 -150 L 0 -196 L 140 -150 Z" fill={`url(#${g})`} className="zx-blk zx-blk--near" />
      <rect x={-3} y={-238} width="5" height="44" className="zx-blk__mast" />
      <path className="zx-st__lamp" d="M 2 -238 L 36 -227 L 2 -216 Z" />
      <Row x={-106} y={-128} n={6} gap={35} lit />
      <Row x={-106} y={-96} n={6} gap={35} />
      <Blk x={-24} y={-48} w={48} h={48} g={g} tone="near" r={2} />
    </>
  ),
  // Robotics labs — glass frontage with a bench arm inside
  () => (
    <>
      <Pad half={138} />
      <Blk x={-116} y={-158} w={232} h={158} g={g} tone="mid" r={4} />
      <Blk x={-96} y={-140} w={192} h={74} g={g} tone="near" r={3} />
      <Blk x={-42} y={-90} w={28} h={18} g={g} tone="near" r={3} />
      <rect className="zx-st__arm" x={-34} y={-130} width="13" height="46" rx="5"
        transform="rotate(-20 -28 -90)" />
      <rect className="zx-st__arm" x={-9} y={-138} width="13" height="36" rx="5"
        transform="rotate(28 -3 -126)" />
      <circle className="zx-st__lamp" cx="54" cy="-104" r="13" />
      <Row x={-100} y={-48} n={5} gap={42} />
      <Roof x={-20} y={-158} g={g} mast={18} />
    </>
  ),
  // Cloud learning — library with a dome
  () => (
    <>
      <Pad half={142} />
      <Blk x={-118} y={-138} w={236} h={138} g={g} tone="mid" />
      <path d="M -76 -138 A 76 76 0 0 1 76 -138 Z" fill={`url(#${g})`} className="zx-blk zx-blk--near" />
      <rect x={-3} y={-238} width="5" height="26" className="zx-blk__mast" />
      <rect className="zx-st__lamp" x={-15} y={-218} width="30" height="13" rx="4" />
      <Row x={-98} y={-116} n={3} gap={31} lit />
      <Row x={24} y={-116} n={3} gap={31} lit />
      <Row x={-98} y={-74} n={6} gap={35} />
    </>
  ),
  // Campus monitoring — gate, camera masts
  () => (
    <>
      <Pad half={150} />
      <Blk x={-142} y={-122} w={42} h={122} g={g} tone="mid" r={3} />
      <Blk x={100} y={-122} w={42} h={122} g={g} tone="mid" r={3} />
      <Blk x={-142} y={-144} w={284} h={24} g={g} tone="near" r={4} />
      <rect className="zx-st__lamp" x={-32} y={-138} width="64" height="12" rx="3" />
      <Blk x={-74} y={-78} w={130} h={50} g={g} tone="far" r={6} />
      <rect className="zx-win zx-win--lit" x={-62} y={-68} width="36" height="22" rx="3" />
      <rect className="zx-win" x={-18} y={-68} width="36" height="22" rx="3" />
      <circle className="zx-st__lamp" cx="-121" cy="-154" r="5.5" />
      <circle className="zx-st__lamp" cx="121" cy="-154" r="5.5" />
    </>
  ),
  // spare — admin block
  () => (
    <>
      <Pad half={130} />
      <Blk x={-108} y={-168} w={216} h={168} g={g} tone="mid" />
      <Row x={-88} y={-146} n={5} gap={37} lit />
      <Row x={-88} y={-114} n={5} gap={37} />
      <Row x={-88} y={-82} n={5} gap={37} />
      <Roof x={-30} y={-168} g={g} mast={22} />
    </>
  ),
  // spare — sports hall
  () => (
    <>
      <Pad half={142} />
      <Blk x={-120} y={-110} w={240} h={110} g={g} tone="mid" r={4} />
      <path d="M -130 -110 A 130 62 0 0 1 130 -110 Z" fill={`url(#${g})`} className="zx-blk zx-blk--near" />
      <rect x={-90} y={-194} width="6" height="84" className="zx-blk__mast" />
      <rect x={84} y={-194} width="6" height="84" className="zx-blk__mast" />
      <circle className="zx-st__lamp" cx="-87" cy="-200" r="8" />
      <circle className="zx-st__lamp" cx="87" cy="-200" r="8" />
      <Row x={-98} y={-80} n={6} gap={35} />
    </>
  ),
];

const KITS = { '01': HEALTHCARE, '02': ENERGY, '03': INDUSTRIAL, '04': EDUCATION };

/* ── which drawing belongs to which service ────────────────────────────────
   By SLUG, not by position. Keying on the index means the kit's third drawing
   lands on whatever the sector's third service happens to be, and the moment
   anybody reorders `sectorServicesData` the solar array is captioned "DG
   Automation" and nobody notices for a month. */
const BY_SLUG = {
  'remote-patient-monitoring': 0,
  'phototherapy-treatment-systems': 1,
  'neonatal-care-solutions': 2,
  'nutrition-assessment-digital-health': 3,
  'connected-medical-devices': 4,

  'energy-monitoring-analytics': 0,
  'battery-energy-storage-monitoring': 1,
  'renewable-energy-solar-monitoring': 2,
  'predictive-maintenance-industrial-iot': 5,
  'dg-automation-remote-control': 4,
  'industrial-electrical-asset-monitoring': 3,

  'smart-factory-machine-monitoring': 0,
  'predictive-maintenance-solutions': 1,
  'industrial-data-acquisition-dashboards': 2,
  'industrial-automation-control-systems': 3,
  'industrial-asset-utility-monitoring': 4,
  'connected-industrial-iot-platforms': 5,

  'stem-iot-learning-kits': 0,
  'robotics-automation-labs': 1,
  'cloud-based-learning-platforms': 2,
  'engineering-lab-development': 3,
};

export function Station({ sectorId, slug, index, gradientId }) {
  const kit = (KITS[sectorId] || HEALTHCARE)(gradientId);
  const at = BY_SLUG[slug] !== undefined ? BY_SLUG[slug] : index;
  const Draw = kit[at] || kit[at % kit.length];
  return <Draw />;
}

/* ── the skyline behind ───────────────────────────────────────────────────
   The blocks in the middle distance. Not silhouettes of anything — at this
   weight nobody reads them, and their job is rate: something at 0.30x that
   proves the distance is moving slower than the thing you are looking at.

   Still built from the same primitive, so they get the same gradient and the
   same light-catch. A far band of plain flat rectangles is exactly what made
   the first version look unfinished. */
export function StationFar({ index, gradientId }) {
  const seed = (index * 2654435761) >>> 0;
  const rnd = k => ((seed >> (k * 4)) & 15) / 15;
  return (
    <>
      {[0, 1, 2, 3, 4].map(i => {
        const w = 46 + rnd(i) * 62;
        const h = 96 + rnd(i + 2) * 156;
        const x = -210 + i * 92 + rnd(i + 1) * 18;
        return (
          <React.Fragment key={i}>
            <Blk x={x} y={-h} w={w} h={h} g={gradientId} tone="far" r={2.5} />
            {rnd(i + 3) > 0.55 && (
              <rect x={x + w * 0.4} y={-h - 22} width="3" height="22" className="zx-blk__mast" />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
